import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// 🧡 FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as faSolidHeart, faPhone } from "@fortawesome/free-solid-svg-icons";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { getFavouritesByUser, removeFavourite } from "../services/favouriteService";
import "../styles/user/Favourite.css";

export default function Favourite() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [favourites, setFavourites] = useState([]);
  const [page, setPage] = useState(1);
  const PER_PAGE = 6;

  /* === Cuộn lên đầu khi load trang === */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  /* === Lấy danh sách yêu thích === */
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        const data = await getFavouritesByUser();
        if (mounted) setFavourites(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Lỗi lấy favourites:", err);
        if (mounted) setFavourites([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      
    };
  }, []);

  /* === Phân trang === */
  const totalPages = Math.max(1, Math.ceil(favourites.length / PER_PAGE));
  const paged = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return favourites.slice(start, start + PER_PAGE);
  }, [favourites, page]);

  /* === Helper === */
  const getRoom = (fav) => fav?.room_id || fav;
  const roomId = (fav) => getRoom(fav)?._id || fav?._id;
  const roomTitle = (fav) =>
    getRoom(fav)?.apartmentName || getRoom(fav)?.title || "Không rõ";
  const roomPrice = (fav) => getRoom(fav)?.price || 0;
  const roomImage = (fav) =>
    getRoom(fav)?.images?.[0]?.url ||
    "https://via.placeholder.com/600x360?text=No+Image";

  const shortAddress = (address) => {
    if (!address) return "";
    const parts = address.split(",");
    let result = parts.slice(-2).join(",").trim();
    result = result.replace(/Thành phố\s+/gi, "");
    result = result.replace(/Tỉnh\s+/gi, "");
    return result;
  };

  const roomAddr = (fav) => {
    const r = getRoom(fav);
    return shortAddress(r?.address || r?.location || "");
  };

  /* === Bỏ yêu thích === */
  const handleUnlike = async (e, favouriteId) => {
    e.stopPropagation();
    try {
      await removeFavourite(favouriteId);
      setFavourites((prev) => prev.filter((f) => f._id !== favouriteId));

      // Delay nhẹ để tránh toast gọi trước khi DOM ổn định
      setTimeout(() => {
        toast.success(" Đã bỏ yêu thích!", {
          position: "top-right",
          autoClose: 1000, // 🕒 tự tắt sau 2 giây
          closeOnClick: true,
          pauseOnHover: false,
          theme: "colored",
        });
      }, 100);
    } catch (err) {
      console.error("❌ Bỏ thích thất bại:", err);
      setTimeout(() => {
        toast.error("Không thể bỏ thích. Vui lòng thử lại!", {
          position: "top-right",
          autoClose: 2000,
          closeOnClick: true,
          pauseOnHover: false,
          theme: "colored",
        });
      }, 100);
    }
  };

  return (
    <div className="favourite-page">
      <Header />

      {/* 🧡 Toast hiển thị cục bộ, autoClose + không crash */}
      <ToastContainer
        position="top-right"
        autoClose={2000}
        limit={2}
        closeOnClick
        pauseOnHover={false}
        theme="colored"
      />

      <section className="hero">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="hero-title">
            Danh sách <span className="highlight">yêu thích</span>
          </h1>
          <p className="hero-desc">
            Lưu lại những căn phòng bạn ưng ý.{" "}
            <span className="highlight">Quay lại sau</span> chỉ với một cú nhấp!
          </p>
        </motion.div>
      </section>

      <section className="fav-section">
        {loading ? (
          <p>Đang tải dữ liệu...</p>
        ) : favourites.length === 0 ? (
          <p className="empty-text">Bạn chưa có phòng nào trong danh sách yêu thích.</p>
        ) : (
          <div className="card-grid">
            <AnimatePresence>
              {paged.map((fav) => {
                const room = getRoom(fav);
                return (
                  <motion.div
                    key={fav._id}
                    className="fav-card"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.25 }}
                    onClick={() => navigate(`/room/${roomId(fav)}`)}
                  >
                    <div className="img-wrap">
                      <img src={roomImage(fav)} alt={roomTitle(fav)} />
                      <div className="price-tag">
                        {roomPrice(fav).toLocaleString("vi-VN")} VND/tháng
                      </div>
                    </div>

                    <div className="card-content">
                      <h3>{roomTitle(fav)}</h3>
                      <p className="meta">
                        phòng trọ • {room?.area || "??"} m² • {roomAddr(fav)}
                      </p>

                      <div className="owner-row">
                        <img
                          src={room?.createdBy?.avatar || "/admin.png"}
                          alt={room?.createdBy?.name || "Chủ phòng"}
                        />
                        <div>
                          <div className="owner-name">
                            {room?.createdBy?.name || "Người đăng"}
                          </div>
                          <div className="owner-date">
                            {room?.createdAt
                              ? new Date(room.createdAt).toLocaleDateString("vi-VN")
                              : ""}
                          </div>
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          className="call-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/room/${roomId(fav)}`);
                          }}
                        >
                          <FontAwesomeIcon icon={faPhone} /> 0123 222 ...
                        </button>

                        <button
                          className="like-btn"
                          onClick={(e) => handleUnlike(e, fav._id)}
                        >
                          <FontAwesomeIcon icon={faSolidHeart} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </section>
      {/* Pagination */}
{totalPages > 1 && (
  <div className="fav-pagination">
    <button
      disabled={page === 1}
      onClick={() => setPage(page - 1)}
    >
      «
    </button>

    {Array.from({ length: totalPages }, (_, i) => (
      <button
        key={i}
        className={page === i + 1 ? "active" : ""}
        onClick={() => setPage(i + 1)}
      >
        {i + 1}
      </button>
    ))}

    <button
      disabled={page === totalPages}
      onClick={() => setPage(page + 1)}
    >
      »
    </button>
  </div>
)}


      <Footer />
    </div>
  );
}
